import type { ReactNode } from 'react';
import { PlayIcon } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';

export function extractYouTubeVideoId(url: string): string | null {
	try {
		const parsed = new URL(url);

		if (parsed.hostname === 'youtu.be') return parsed.pathname.slice(1) || null;
		if (parsed.hostname.includes('youtube.com')) {
			if (parsed.pathname.startsWith('/embed/')) {
				return parsed.pathname.replace('/embed/', '') || null;
			}

			if (parsed.pathname.startsWith('/shorts/')) {
				return parsed.pathname.replace('/shorts/', '') || null;
			}

			return parsed.searchParams.get('v');
		}
	} catch {
		// invalid URL
	}

	return null;
}

/**
 * Every state of a gated video renders through this one frame, so moving between them never reflows.
 *
 * The poster repeats the hero image, so its alt is empty unless a title is given.
 */
export function YouTubeFrame(props: { posterSrc?: string; title?: string; className?: string; children?: ReactNode }) {
	return (
		<div className="flex h-full items-center justify-center">
			<div className={cn('bg-muted relative aspect-video w-full overflow-hidden rounded-2xl', props.className)}>
				{props.posterSrc && (
					<img src={props.posterSrc} alt={props.title ?? ''} className="absolute inset-0 size-full object-cover" />
				)}
				{props.children}
			</div>
		</div>
	);
}

/** Just the poster: a "click to load" prompt here would flash for every donor who already accepted. */
export function YouTubePending(props: { posterSrc?: string; title?: string; className?: string }) {
	return <YouTubeFrame posterSrc={props.posterSrc} title={props.title} className={props.className} />;
}

/**
 * The two-click pattern: the embed contacts Google on mount, so it waits for a click or CMP consent.
 *
 * The poster must be first-party, never `i.ytimg.com`, which would contact Google on load.
 */
export function YouTubePlaceholder(props: {
	posterSrc?: string;
	title?: string;
	className?: string;
	texts: { activate: string; notice: string };
	onActivate: () => void;
}) {
	return (
		<YouTubeFrame posterSrc={props.posterSrc} title={props.title} className={props.className}>
			{/* No `aria-label`: it would hide the consent notice from a screen reader. */}
			<button
				type="button"
				onClick={props.onActivate}
				className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 bg-black/50 px-6 py-8 text-center text-white transition-colors hover:bg-black/40"
			>
				<span className="grid size-16 place-items-center rounded-full bg-white/95 transition-transform group-hover:scale-105">
					<PlayIcon className="ml-0.5 size-7 fill-black text-black" />
				</span>
				<span className="text-base font-medium">{props.texts.activate}</span>
				<span className="max-w-md text-xs leading-snug text-white/85">{props.texts.notice}</span>
			</button>
		</YouTubeFrame>
	);
}

/**
 * No poster: the hero behind the player reads as the page changing its mind; black reads as loading.
 *
 * `autoPlay` only after the placeholder's "Play video": a paused `controls=0` frame looks inert. Consent
 * from the CMP still mounts a paused one.
 */
export function YouTubeEmbed(props: { url: string; title?: string; className?: string; autoPlay?: boolean }) {
	const videoId = extractYouTubeVideoId(props.url);
	if (!videoId) return null;

	return (
		<YouTubeFrame className={cn('bg-black', props.className)}>
			{/* oxlint-disable-next-line react/iframe-missing-sandbox */}
			<iframe
				src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&controls=0${props.autoPlay ? '&autoplay=1' : ''}`}
				title={props.title ?? 'Campaign video'}
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				referrerPolicy="strict-origin-when-cross-origin"
				allowFullScreen
				className="absolute inset-0 size-full"
			/>
		</YouTubeFrame>
	);
}
